/* eslint-disable react-hooks/exhaustive-deps */
/**
 * FilterProvider
 */
import _ from 'lodash'
import React, {
  useEffect,
  useMemo,
  useState,
} from 'react'
import FilterContext from './filter-context'

export default function FilterProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false)
  const [content, setContent] = useState({})
  const [data, setData] = useState({})
  const [refinedFilters, setRefinedFilters] = useState({})
  const [loadingSections, setLoadingSections] = useState([])
  const options = useMemo(() => _.get(content, 'options', []), [content])
  const defaultRefinedFilters = useMemo(() => _.get(content, 'defaultRefinedFilters', {}), [content])

  const open = (obj) => {
    setContent(() => (
      _.pick(obj, ['title', 'options', 'defaultRefinedFilters', 'onApply'])
    ))
    setIsOpen(true)
  }
  const onRequestClose = () => {
    setIsOpen(false)
  }

  const onClear = () => {
    // clear data
    setRefinedFilters({})
  }

  const onApply = () => {
    // convert user selected filter to api params
    if (_.isFunction(content.onApply)) {
      content.onApply(refinedFilters)
    }
    onRequestClose()
  }

  const onChange = ({ key, values = [] }) => {
    setRefinedFilters((prevRefindedFilters) => ({
      ...prevRefindedFilters,
      [key]: _.isEmpty(values) ? null : values,
    }))
  }

  const onSearch = ({ key, query }) => {
    const option = _.find(options, { key })
    if (_.isEmpty(option)) return

    const {
      fetchData,
      getFetchDataParams,
      parseData = _.identity,
      data: _data,
    } = option
    if (_.isFunction(fetchData)) {
      setLoadingSections((prev) => _.uniq([...prev, key]))
      fetchData(getFetchDataParams(query))
        .then((response) => {
          setData((prevData) => (
            _.map(prevData, (prevOption) => {
              if (prevOption.key !== key) {
                return prevOption
              }
              return {
                ...prevOption,
                data: parseData(response),
              }
            })
          ))
        })
        .catch(() => {
          setData((prevData) => (
            _.map(prevData, (prevOption) => {
              if (prevOption.key !== key) {
                return prevOption
              }
              return {
                ...prevOption,
                disabled: true,
              }
            })
          ))
        })
        .finally(() => {
          setLoadingSections((prev) => _.without(prev, key))
        })
    } else if (!_.isEmpty(_data)) {
      const filterdData = _.filter(_data, (item) => (
        _.includes(item.name.toLowerCase(), query.toLowerCase())
      ))
      setData((prevData) => (
        _.map(prevData, (prevOption) => {
          if (prevOption.key !== key) {
            return prevOption
          }
          return {
            ...prevOption,
            data: filterdData,
          }
        })
      ))
    }
  }

  useEffect(() => {
    if (!isOpen) {
      setContent({})
    }
  }, [isOpen])

  useEffect(() => {
    setRefinedFilters(defaultRefinedFilters)
  }, [defaultRefinedFilters])

  useEffect(() => {
    const init = _.map(options, (option) => {
      const {
        title,
        key,
        data: _data,
        fetchData,
        value,
        disabled,
        enableSearch,
        selectionType,
        getSubSectionTitle,
      } = option
      const haveChildren = !_.isEmpty(_data) || _.isFunction(fetchData)

      return {
        key,
        name: title,
        selectionType,
        getSubSectionTitle,
        status: _.isFunction(fetchData) ? 'loading' : 'fulfilled',
        data: _data,
        // filterType: key,
        disabled: (_.isNil(value) && !haveChildren) || disabled,
        enableSearch,
      }
    })
    setData(init)

    // [TODO] support pagination
    // [TODO] add loading state
    const optionsNeedFetch = _.filter(options, (option) => _.isFunction(option.fetchData))
    if (!_.isEmpty(optionsNeedFetch)) {
      _.forEach(optionsNeedFetch, ({
        key,
        fetchData,
        getFetchDataParams = () => ({}),
        parseData = _.identity,
      }) => {
        setLoadingSections((prev) => _.uniq([...prev, key]))
        fetchData(getFetchDataParams())
          .then((response) => {
            setData((prevData) => (
              _.map(prevData, (option) => {
                if (option.key !== key) {
                  return option
                }
                return {
                  ...option,
                  status: 'fulfilled',
                  data: parseData(response),
                  onSearch,
                }
              })
            ))
          })
          .catch(() => {
            setData((prevData) => (
              _.map(prevData, (option) => {
                if (option.key !== key) {
                  return option
                }
                return {
                  ...option,
                  status: 'rejected',
                  disabled: true,
                }
              })
            ))
          })
          .finally(() => {
            setLoadingSections((prev) => _.without(prev, key))
          })
      })
    }
  }, [options])

  const resolveFilters = (f) => {
    let refined = null
    if (!_.isEmpty(f)) {
      refined = {}
      _.forEach(Object.keys(f), (k) => {
        if (!_.isEmpty(_.get(f, k))) {
          if (_.isArray(_.get(f, k)) && _.size(_.get(f, k)) === 1) {
            refined[k] = _.first(_.get(f, k))
          } else {
            refined[k] = _.get(f, k)
          }
        }
      })
    }
    return refined
  }

  const state = {
    isOpen,
    open,
    title: _.get(content, 'title'),
    data,
    loadingSections,
    refinedFilters,
    onChange,
    onRequestClose,
    onClear,
    onApply,
    resolveFilters,
  }

  return (
    <FilterContext.Provider value={state}>
      {children}
    </FilterContext.Provider>
  )
}
